import React, { useState } from 'react';
import { Group } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Loader2, AlertCircle, Database } from 'lucide-react';
import DateRangePicker from '../components/DateRangePicker';
import { useDateRange } from '../contexts/DateRangeContext';
import { 
  formatCurrency,
  getGroupsWithCostsFromData,
  getGroupPieDataFromData,
  getGroupTrendDataFromData,
  getGroupTrendDataForTableFromData
} from '../utils/dataProcessor';
import { useCostDataWithFallback } from '../hooks/useCostData';

interface GroupsProps {
  onTabChange?: (tab: string) => void;
}

const Groups: React.FC<GroupsProps> = ({ onTabChange }) => {
  const [showGroupsSeparately, setShowGroupsSeparately] = useState<boolean>(true);
  const { dateRange } = useDateRange();

  // React Query 훅 사용 - 날짜 범위 변경 시 자동으로 새 데이터 가져오기
  const { data: costData, isLoading, isError, error } = useCostDataWithFallback({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    enabled: Boolean(dateRange.startDate && dateRange.endDate)
  });

  console.log('🔄 Groups 컴포넌트 렌더링:', {
    dateRange,
    isLoading,
    isError,
    dataCount: costData?.length || 0,
    error: error?.message || null
  });

  // 새로운 함수들을 사용하여 데이터 처리
  const groups = costData ? getGroupsWithCostsFromData(costData) : [];
  const totalCost = groups.reduce((sum: number, group: Group) => sum + group.totalCost, 0);
  const rawPieData = costData ? getGroupPieDataFromData(costData) : [];
  
  // 0인 값 필터링 및 내림차순 정렬
  const pieData = rawPieData
    .filter((item: any) => item.totalCost > 0)
    .sort((a: any, b: any) => b.totalCost - a.totalCost);

  console.log('📊 처리된 그룹 데이터:', {
    groupsCount: groups.length,
    totalCost,
    pieDataCount: pieData.length,
    sampleGroups: groups.slice(0, 3),
    localStorage_groups: localStorage.getItem('groups'),
    localStorage_users: localStorage.getItem('users')
  });

  // 로딩 상태 처리
  if (isLoading) {
    console.log('⏳ 로딩 상태 렌더링');
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">グループ</h1>
              <p className="text-gray-600">グループ別に整理されたコスト分析</p>
            </div>
          </div>
        </div>

        {/* Date Range Picker */}
        <DateRangePicker />

        {/* Loading Content */}
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-gray-600">Loading...</h2>
          </div>
        </div>
      </div>
    );
  }

  // 에러 상태 처리
  if (isError) {
    console.log('❌ 에러 상태 렌더링:', error);
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">グループ</h1>
              <p className="text-gray-600">グループ別に整理されたコスト分析</p>
            </div>
          </div>
        </div>

        {/* Date Range Picker */}
        <DateRangePicker />

        {/* Error Content */}
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-red-600 mx-auto mb-4" />
            <p className="text-red-600 font-medium mb-2">데이터 로드 중 오류가 발생했습니다</p>
            <p className="text-gray-600 text-sm">
              {error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다'}
            </p>
            <p className="text-gray-500 text-xs mt-2">
              날짜 범위: {dateRange.startDate} ~ {dateRange.endDate}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 데이터가 없는 경우
  if (!costData || costData.length === 0) {
    console.log('📭 데이터 없음 상태 렌더링');
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">グループ</h1>
              <p className="text-gray-600">グループ別に整理されたコスト分析</p>
            </div>
          </div>
        </div>

        {/* Date Range Picker */}
        <DateRangePicker />

        {/* No Data Content */}
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Database className="h-8 w-8 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">해당 기간에 데이터가 없습니다</p>
            <p className="text-gray-500 text-sm mt-2">다른 날짜 범위를 선택해보세요</p>
            <p className="text-gray-500 text-xs mt-2">
              현재 날짜 범위: {dateRange.startDate} ~ {dateRange.endDate}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // 그룹이 설정되지 않은 경우
  if (groups.length === 0) {
    console.log('👥 그룹 설정 없음 상태 렌더링');
    return (
      <div className="p-8 bg-gray-50 min-h-screen">
        <div className="mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">グループ</h1>
              <p className="text-gray-600">グループ別に整理されたコスト分析</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500 mb-1">총 데이터</p>
              <p className="text-3xl font-bold text-gray-900">{costData.length}개 항목</p>
              <p className="text-sm text-gray-500">선택 기간</p>
            </div>
          </div>
        </div>
        
        {/* Date Range Picker */}
        <DateRangePicker />

        {/* Data Info Badge */}
        <div className="flex items-center space-x-2 mb-6">
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            📊 {costData.length}개 항목
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            📅 {dateRange.startDate} ~ {dateRange.endDate}
          </span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            ⚠️ 그룹 설정 없음
          </span>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">グループが設定されていません</h3>
          <p className="text-gray-500 mb-4">設定でグループを作成して、コストデータを整理します</p>
          <div className="bg-gray-100 p-4 rounded-lg mb-4 text-sm text-left">
            <p className="font-medium text-gray-700 mb-2">디버깅 정보:</p>
            <p className="text-gray-600">• 총 데이터 항목: {costData.length}개</p>
            <p className="text-gray-600">• 설정된 그룹: {groups.length}개</p>
            <p className="text-gray-600">• localStorage 그룹: {localStorage.getItem('groups') ? '있음' : '없음'}</p>
          </div>
          <button 
            onClick={() => onTabChange && onTabChange('settings')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            設定に移動
          </button>
        </div>
      </div>
    );
  }

  console.log('✅ 정상 UI 렌더링 진행');

  const getGroupColors = () => {
    return [
      { bg: 'bg-blue-500', text: 'text-blue-500', stroke: '#3B82F6' },
      { bg: 'bg-green-500', text: 'text-green-500', stroke: '#10B981' },
      { bg: 'bg-purple-500', text: 'text-purple-500', stroke: '#8B5CF6' },
      { bg: 'bg-yellow-500', text: 'text-yellow-500', stroke: '#F59E0B' },
      { bg: 'bg-red-500', text: 'text-red-500', stroke: '#EF4444' },
      { bg: 'bg-indigo-500', text: 'text-indigo-500', stroke: '#6366F1' },
      { bg: 'bg-pink-500', text: 'text-pink-500', stroke: '#EC4899' },
      { bg: 'bg-teal-500', text: 'text-teal-500', stroke: '#14B8A6' }
    ];
  };

  const colors = getGroupColors();

  // 파이 차트용 SVG 경로 생성 함수
  const createPieSlice = (cx: number, cy: number, radius: number, startAngle: number, endAngle: number) => {
    const start = polarToCartesian(cx, cy, radius, endAngle);
    const end = polarToCartesian(cx, cy, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
      "M", cx, cy, 
      "L", start.x, start.y, 
      "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
      "Z"
    ].join(" ");
  };

  const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: centerX + (radius * Math.cos(angleInRadians)),
      y: centerY + (radius * Math.sin(angleInRadians))
    };
  };

  let cumulativeAngle = 0;

  return (
    <div className="p-8 bg-gray-50 min-h-screen">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">グループ</h1>
            <p className="text-gray-600">グループ別に整理されたコスト分析</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 mb-1">総コスト</p>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalCost)}</p>
            <p className="text-sm text-gray-500">選択期間</p>
          </div>
        </div>
      </div>

      {/* Date Range Picker */}
      <DateRangePicker />

      {/* Data Info Badge */}
      <div className="flex items-center space-x-2 mb-6">
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          📊 {costData.length}개 항목
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          📅 {dateRange.startDate} ~ {dateRange.endDate}
        </span>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
          👥 {groups.length}개 그룹
        </span>
      </div>

      {/* Groups Section with Pie Chart */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">グループ</h3>
        
        <div className="flex items-center justify-center mb-4">
          <svg width="160" height="160" viewBox="0 0 160 160" className="transform -rotate-90">
            {pieData.map((item: any, index: number) => {
              const startAngle = cumulativeAngle;
              const endAngle = cumulativeAngle + (item.percentage / 100) * 360;
              cumulativeAngle = endAngle;
              
              return (
                <path
                  key={item.id}
                  d={createPieSlice(80, 80, 60, startAngle, endAngle)}
                  fill={item.color.stroke}
                  stroke="white"
                  strokeWidth="2"
                />
              );
            })}
            {/* 중앙 원 */}
            <circle cx="80" cy="80" r="28" fill="white" />
          </svg>
        </div>

        <div className="space-y-3">
          {pieData.map((item: any) => (
            <div key={item.id} className="flex items-center justify-between">
              <div className="flex items-center">
                <div 
                  className="w-4 h-4 rounded-full mr-3 border-2 border-white shadow-sm"
                  style={{ backgroundColor: item.color.stroke }}
                ></div>
                <span className="text-sm font-medium text-gray-900">{item.name}</span>
              </div>
              <div className="text-right">
                <div className="text-sm font-semibold text-gray-900">{formatCurrency(item.totalCost)}</div>
                <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost Trends */}
      <div className="bg-white p-6 rounded-lg shadow-sm mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">コストトレンド</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">合計</span>
              <button
                onClick={() => setShowGroupsSeparately(!showGroupsSeparately)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  showGroupsSeparately ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    showGroupsSeparately ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="text-sm text-gray-600">個別</span>
            </div>
          </div>
          
          {(() => {
            const chartData = getGroupTrendDataFromData(costData, showGroupsSeparately);
            
            console.log('📈 Chart data generation result:', {
              chartDataLength: chartData.length,
              showGroupsSeparately,
              firstDataPoint: chartData[0],
              lastDataPoint: chartData[chartData.length - 1]
            });
            
            if (chartData.length === 0) {
              console.log('⚠️ Chart data is empty');
              return (
                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                  選択した期間に、選択したグループの利用可能な日次データがありません
                </div>
              );
            }
            
            if (showGroupsSeparately) {
              // 실제 데이터가 있는 그룹들만 추출
              const activeGroups = pieData.map((item: any) => ({
                id: item.id,
                name: item.name,
                color: item.color
              }));
              
              return (
                <div>
                  {/* 범례 - 실제 데이터가 있는 그룹만 표시 */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    {activeGroups.map((group: any, index: number) => (
                      <div key={group.id} className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: group.color.stroke }}
                        ></div>
                        <span className="text-sm text-gray-600">{group.name}</span>
                      </div>
                    ))}
                  </div>
                  
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tickFormatter={(value) => `$${value}`} />
                      <Tooltip
                        formatter={(value, name) => {
                          const group = activeGroups.find((g: any) => g.id === name);
                          return [formatCurrency(value as number), group?.name || name];
                        }}
                        labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                      />
                      {/* 실제 데이터가 있는 그룹들만 라인 표시 */}
                      {activeGroups.map((group: any, index: number) => (
                        <Line 
                          key={group.id}
                          type="monotone" 
                          dataKey={group.id} 
                          stroke={group.color.stroke} 
                          strokeWidth={2}
                          dot={{ fill: group.color.stroke, strokeWidth: 2, r: 3 }}
                          activeDot={{ r: 5, fill: group.color.stroke }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              );
            } else {
              return (
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    />
                    <YAxis tickFormatter={(value) => `$${value}`} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value as number)}
                      labelFormatter={(label) => new Date(label).toLocaleDateString('en-US')}
                    />
                    <Line type="monotone" dataKey="totalCost" stroke="#3b82f6" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              );
            }
          })()}
      </div>

      {/* Group Details Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="px-6 py-3 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">グループ別コスト詳細</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="w-1/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="w-2/5 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pieData.map((item: any, index: number) => {
                const trendData = getGroupTrendDataForTableFromData(item, costData || []);
                const maxValue = Math.max(...trendData, 1);
                
                return (
                  <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div 
                          className="w-3 h-3 rounded-full mr-3 flex-shrink-0"
                          style={{ backgroundColor: item.color.stroke }}
                        ></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{item.name}</div>
                          <div className="text-xs text-gray-500">{item.members.length} member{item.members.length !== 1 ? 's' : ''}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-bold text-gray-900">{formatCurrency(item.totalCost)}</div>
                      <div className="text-xs text-gray-500">{item.percentage.toFixed(1)}% of total</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="w-40 h-12 mr-4">
                          <svg width="160" height="48" viewBox="0 0 160 48" className="overflow-visible">
                            <defs>
                              <linearGradient id={`gradient-${item.id}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor={item.color.stroke} stopOpacity="0.3"/>
                                <stop offset="100%" stopColor={item.color.stroke} stopOpacity="0.1"/>
                              </linearGradient>
                            </defs>
                            
                            {/* Grid lines for better readability */}
                            <defs>
                              <pattern id={`grid-${item.id}`} width="20" height="48" patternUnits="userSpaceOnUse">
                                <path d="M 20 0 L 0 0 0 48" fill="none" stroke="#f3f4f6" strokeWidth="0.5"/>
                              </pattern>
                            </defs>
                            <rect width="160" height="48" fill={`url(#grid-${item.id})`} />
                            
                            {/* Trend line */}
                            <polyline
                              fill="none"
                              stroke={item.color.stroke}
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              points={trendData.map((value, idx) => {
                                const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                                const y = 48 - (value / maxValue) * 36 - 6;
                                return `${x},${y}`;
                              }).join(' ')}
                            />
                            
                            {/* Fill area under the curve */}
                            <polygon
                              fill={`url(#gradient-${item.id})`}
                              points={`${trendData.map((value, idx) => {
                                const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                                const y = 48 - (value / maxValue) * 36 - 6;
                                return `${x},${y}`;
                              }).join(' ')} 160,48 0,48`}
                            />
                            
                            {/* Data points */}
                            {trendData.map((value, idx) => {
                              const x = (idx / Math.max(trendData.length - 1, 1)) * 160;
                              const y = 48 - (value / maxValue) * 36 - 6;
                              return (
                                <circle
                                  key={idx}
                                  cx={x}
                                  cy={y}
                                  r="2"
                                  fill={item.color.stroke}
                                  className="opacity-60"
                                />
                              );
                            })}
                          </svg>
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Groups;